import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  Play,
  Store,
  Tablet,
  Users,
  UtensilsCrossed,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";

export interface PosReadinessStatus {
  isReady: boolean;
  steps: {
    hasActiveSubscription: boolean;
    hasLocation: boolean;
    hasMenu: boolean;
    hasTerminalLocation: boolean;
    hasEmployeeAssigned: boolean;
    hasCompletedFirstSession: boolean;
  };
  details: {
    locations: Array<{
      id: string;
      name: string;
      hasTerminalLocation: boolean;
      terminalLocationId?: string | null;
      employeeCount: number;
      employees: Array<{ id: string; name: string } | null>;
    }>;
    totalLocations?: number;
    totalMenus?: number;
    locationsWithTerminalLocation?: number;
    totalEmployees?: number;
  };
}

function PosReadinessItem({
  to,
  isComplete,
  icon: Icon,
  title,
  completeText,
  incompleteText,
  showArrow = true,
}: {
  to: string;
  isComplete: boolean;
  icon: React.ElementType;
  title: string;
  completeText: string;
  incompleteText: string;
  showArrow?: boolean;
}) {
  const content = (
    <div className="flex items-center gap-3">
      <div
        className={`flex size-10 items-center justify-center rounded-full ${isComplete ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}
      >
        {isComplete ? (
          <CheckCircle2 className="size-5" />
        ) : (
          <Icon className="size-5" />
        )}
      </div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-muted-foreground text-sm">
          {isComplete ? completeText : incompleteText}
        </p>
      </div>
    </div>
  );

  if (!showArrow) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-4 transition-colors">
        {content}
      </div>
    );
  }

  return (
    <Link
      className="hover:bg-muted/50 flex items-center justify-between rounded-lg border p-4 transition-colors"
      to={to}
    >
      {content}
      <ArrowRight className="text-muted-foreground size-5" />
    </Link>
  );
}

export function SetupPosStep({
  posReadiness,
}: {
  posReadiness: PosReadinessStatus | undefined;
}) {
  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader className="text-center">
        <div className="bg-primary/10 mx-auto mb-4 flex size-16 items-center justify-center rounded-full">
          <Store className="text-primary size-8" />
        </div>
        <CardTitle className="h2">Finish Setting Up Your POS</CardTitle>
        <CardDescription className="body">
          Complete these steps to start using your POS system
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <PosReadinessItem
          completeText={`${posReadiness?.details.totalLocations ?? 0} ${(posReadiness?.details.totalLocations ?? 0) === 1 ? "location" : "locations"} added`}
          icon={Store}
          incompleteText="Create your first location"
          isComplete={posReadiness?.steps.hasLocation ?? false}
          title="Add a Location"
          to="/locations"
        />

        <PosReadinessItem
          completeText={`${posReadiness?.details.totalMenus ?? 0} ${(posReadiness?.details.totalMenus ?? 0) === 1 ? "menu" : "menus"} created`}
          icon={UtensilsCrossed}
          incompleteText="Add items your customers can order"
          isComplete={posReadiness?.steps.hasMenu ?? false}
          title="Create a Menu"
          to="/menus"
        />

        <PosReadinessItem
          completeText={`${posReadiness?.details.locationsWithTerminalLocation ?? 0} ${(posReadiness?.details.locationsWithTerminalLocation ?? 0) === 1 ? "location" : "locations"} ready for readers`}
          icon={Tablet}
          incompleteText="Order card readers for your locations"
          isComplete={posReadiness?.steps.hasTerminalLocation ?? false}
          title="Order Terminal Hardware"
          to="/settings/terminals"
        />

        <PosReadinessItem
          completeText={`${posReadiness?.details.totalEmployees ?? 0} ${(posReadiness?.details.totalEmployees ?? 0) === 1 ? "employee" : "employees"} assigned`}
          icon={Users}
          incompleteText="Create employees to use the POS"
          isComplete={posReadiness?.steps.hasEmployeeAssigned ?? false}
          title="Add an Employee"
          to="/employees"
        />

        <PosReadinessItem
          completeText="POS is active and ready"
          icon={Play}
          incompleteText="Open the Plasma POS app and sign in"
          isComplete={posReadiness?.steps.hasCompletedFirstSession ?? false}
          showArrow={false}
          title="Start Your First POS Session"
          to=""
        />

        <p className="text-muted-foreground pt-2 text-center text-sm">
          Complete all steps above to finish onboarding
        </p>
      </CardContent>
    </Card>
  );
}
